import React, { useState } from 'react'

import { Modal } from '@components/common/modal'
import { VendorPricingAgreementTiers } from '@components/common'

import {
  VendorPricingAgreement,
  VendorPricingAgreementServiceType,
  VendorPricingAgreementTier,
  VendorPricingAgreementDiscountType,
  VendorPricingAgreementDiscountTypes,
} from '@types'

interface EditVendorPricingAgreementModalProps {
  currentUser: { firstName: string; lastName: string; email: string }
  vendorPricingAgreement: VendorPricingAgreement | null

  saveVendorPricingAgreement: (
    data: VendorPricingAgreement,
  ) => Promise<VendorPricingAgreement | null>
  close: () => void
}

const EditVendorPricingAgreementModal: React.FC<
  EditVendorPricingAgreementModalProps
> = ({
  currentUser,
  vendorPricingAgreement,
  saveVendorPricingAgreement,
  close,
}) => {
  const [pricingAgreement, setVendorPricingAgreement] =
    useState<VendorPricingAgreement | null>(
      vendorPricingAgreement
        ? {
            ...vendorPricingAgreement,
            lastUpdatedBy: `${currentUser.firstName} ${currentUser.lastName} (${currentUser.email})`,
          }
        : null,
    )

  if (!pricingAgreement) {
    return null
  }

  const onChangeActive = (value: boolean) => {
    pricingAgreement &&
      setVendorPricingAgreement({ ...pricingAgreement, active: value })
  }

  const onChangeDiscountType = (
    discountType: VendorPricingAgreementDiscountType,
  ) => {
    if (!pricingAgreement) {
      return
    }

    // When changing discount type, update the tiers to have appropriate fields
    const updatedTiers = pricingAgreement.tiers.map((tier) => {
      const baseTier = {
        lowerLimit: tier.lowerLimit,
        upperLimit: tier.upperLimit,
        position: tier.position,
      }

      if (discountType === VendorPricingAgreementDiscountTypes.PERCENTAGE) {
        return {
          ...baseTier,
          discountPercentage: tier.discountPercentage ?? 1,
        }
      } else {
        return {
          ...baseTier,
          fixedEntreePrice: tier.fixedEntreePrice,
          fixedSidePrice: tier.fixedSidePrice,
        }
      }
    })

    setVendorPricingAgreement({
      ...pricingAgreement,
      discountType,
      tiers: updatedTiers,
    })
  }

  const onChangeTiers =
    (idx: number, field: keyof VendorPricingAgreementTier) =>
    (value: number | undefined) => {
      const tiers = pricingAgreement?.tiers || []
      const newTiers = [...tiers]
      // Convert undefined to null for upperLimit (null represents "unlimited")
      const newValue =
        field === 'upperLimit' && value === undefined ? null : value
      newTiers[idx][field] = newValue as any
      pricingAgreement &&
        setVendorPricingAgreement({ ...pricingAgreement, tiers: newTiers })
    }

  const onAddTier = () => {
    const newTier: VendorPricingAgreementTier = {
      position: pricingAgreement.tiers.length + 1,
      lowerLimit: 0,
      upperLimit: null,
    }

    // Add discount type specific fields
    if (
      pricingAgreement.discountType ===
      VendorPricingAgreementDiscountTypes.PERCENTAGE
    ) {
      newTier.discountPercentage = 1
    } else {
      newTier.fixedEntreePrice = undefined
      newTier.fixedSidePrice = undefined
    }

    const nextTiers = [...(pricingAgreement.tiers || []), newTier]
    nextTiers.forEach((tier, index) => {
      tier.position = index + 1
    })
    pricingAgreement &&
      setVendorPricingAgreement({ ...pricingAgreement, tiers: nextTiers })
  }

  const onRemoveTier = (index: number) => {
    const nextTiers = [...(pricingAgreement.tiers || [])]
    nextTiers.splice(index, 1)
    nextTiers.forEach((tier, index) => {
      tier.position = index + 1
    })
    pricingAgreement &&
      setVendorPricingAgreement({ ...pricingAgreement, tiers: nextTiers })
  }

  const onChangeServiceTypes = (
    serviceType: VendorPricingAgreementServiceType,
    checked: boolean,
  ) => {
    const nextServiceTypes = [...(pricingAgreement.serviceTypes || [])]
    if (checked) {
      nextServiceTypes.push(serviceType)
    } else {
      nextServiceTypes.splice(nextServiceTypes.indexOf(serviceType), 1)
    }
    pricingAgreement &&
      setVendorPricingAgreement({
        ...pricingAgreement,
        serviceTypes: nextServiceTypes,
      })
  }

  const onSave = async () => {
    const updatedPricingAgreement =
      await saveVendorPricingAgreement(pricingAgreement)
    if (updatedPricingAgreement) {
      close()
    }
  }

  return (
    <Modal
      title={
        pricingAgreement?.id === ''
          ? 'Create Universal Vendor Pricing Agreement'
          : 'Edit Universal Vendor Pricing Agreement'
      }
      noHeader={true}
      hideModal={close}
      color="#001940"
      width="900px"
    >
      <VendorPricingAgreementTiers
        pricingAgreement={pricingAgreement}
        onChangeActive={onChangeActive}
        onChangeDiscountType={onChangeDiscountType}
        onChangeServiceTypes={onChangeServiceTypes}
        onChangeTiers={onChangeTiers}
        onAddTier={onAddTier}
        onRemoveTier={onRemoveTier}
        onSave={onSave}
        onCancel={close}
      />
    </Modal>
  )
}

export default EditVendorPricingAgreementModal
